import { PDFDocument, PDFName, PDFArray, PDFDict, rgb } from 'pdf-lib';
import { extractOverlayMetadata, embedOverlayMetadata } from './overlayMetadata';

import { isolatePageDrawings } from './pdfFormIsolation';
import { removeUnreachableObjects } from './pdfAppearanceFlatten';
import { rewriteWithPdfium } from './pdfiumEngine';
const overlaps = (a, b) => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];
const numberArray = (array) => array?.asArray().map((value) => value.asNumber());

export function hasDigitalSignature(document) {
  if (!document.catalog.has(PDFName.of('AcroForm'))) return false;
  return document.getForm().getFields().some((field) => field.acroField.getInheritableAttribute(PDFName.of('FT'))?.toString() === '/Sig' &&
    document.context.lookup(field.acroField.getInheritableAttribute(PDFName.of('V'))) instanceof PDFDict);
}

// Editor geometry has a top-left origin in the unrotated MediaBox space.
export function removalTargets(edits, { preview = false, selected = [] } = {}) {
  const selection = new Set(selected);
  return (edits || []).filter((edit) => edit.type === 'text' && !edit.rotationLocked &&
    !edit.sourceRemoved && (edit.source === 'detected' && edit.originalBounds || edit.bakedBounds && !edit.apCarried) &&
    (edit.modified || edit.deleted || edit.restored && edit.bakedBounds && !edit.apCarried || preview && (selection.has(edit.id) || edit.restored)))
    .flatMap((edit) => (edit.sourceTextBounds?.length ? edit.sourceTextBounds : [edit.originalBounds || edit.bakedBounds])
      .map((bounds) => ({ pageNum: edit.pageNum, kind: 'text', topLeft: true, ...bounds })));
}

export async function removePdfContent(bytes, targets, { redact = false, fillColor, onProgress = () => {} } = {}) {
  if (!targets.length) return new Uint8Array(bytes);
  const source = await PDFDocument.load(bytes);
  const pages = source.getPages();
  const regions = targets.map((target) => {
    const pageNum = target.pageNum || target.pageNumber;
    const page = pages[pageNum - 1];
    if (!page || ![target.x, target.y, target.width, target.height].every(Number.isFinite) || target.width <= 0 || target.height <= 0) {
      throw new Error('A content-removal area is invalid. No changes were saved.');
    }
    const y = target.topLeft ? page.getHeight() - target.y - target.height : target.y;
    return { pageNum, kind: target.kind || 'all', baseline: target.baseline, rect: [target.x, y, target.x + target.width, y + target.height] };
  });

  // Rewriting a signed document invalidates its signatures; fail before mutation.
  if (hasDigitalSignature(source)) {
    throw new Error('This document contains a digital signature. Open an unsigned copy before editing or redacting content.');
  }
  if (redact) sanitizeForRedaction(source, regions);
  isolatePageDrawings(source, regions.map(region => region.pageNum));
  removeUnreachableObjects(source);
  const input = await source.save({ useObjectStreams: true, updateFieldAppearances: false });
  let output = await rewriteWithPdfium(input, regions, onProgress);
  if (redact && fillColor) {
    const filled = await PDFDocument.load(output);
    for (const { pageNum, rect } of regions) filled.getPage(pageNum - 1).drawRectangle({
      x: rect[0], y: rect[1], width: rect[2] - rect[0], height: rect[3] - rect[1],
      color: rgb(fillColor.r / 255, fillColor.g / 255, fillColor.b / 255), borderWidth: 0,
    });
    output = await filled.save({ useObjectStreams: true, updateFieldAppearances: false });
  }
  onProgress(1);
  return output;
}

function sanitizeForRedaction(document, regions) {
  const ctx = document.context;
  const removedWidgets = new Set();
  const removedAnnots = new Set();
  // Resolve named internal links before removing the names tree. This keeps
  // navigation working without retaining attachment names or script entries.
  const destinations = new Map();
  const readNames = (node) => {
    if (!node) return;
    const names = node.lookupMaybe(PDFName.of('Names'), PDFArray);
    if (names) for (let i = 0; i + 1 < names.size(); i += 2) destinations.set(names.get(i).decodeText(), names.get(i + 1));
    const kids = node.lookupMaybe(PDFName.of('Kids'), PDFArray);
    kids?.asArray().forEach((ref) => readNames(ctx.lookup(ref, PDFDict)));
  };
  const names = document.catalog.lookupMaybe(PDFName.of('Names'), PDFDict);
  readNames(names?.lookupMaybe(PDFName.of('Dests'), PDFDict));
  const legacyDests = document.catalog.lookupMaybe(PDFName.of('Dests'), PDFDict);
  legacyDests?.entries().forEach(([name, value]) => destinations.set(name.decodeText(), value));
  const resolveDestination = (dict, key) => {
    const value = dict.get(PDFName.of(key));
    if (!value?.decodeText) return;
    let target = destinations.get(value.decodeText());
    if (target) {
      target = ctx.lookup(target);
      dict.set(PDFName.of(key), target instanceof PDFDict ? target.get(PDFName.of('D')) : target);
    } else dict.delete(PDFName.of(key));
  };
  for (const [index, page] of document.getPages().entries()) {
    const pageRegions = regions.filter((region) => region.pageNum === index + 1);
    const annotations = page.node.Annots();
    if (annotations) for (let i = annotations.size() - 1; i >= 0; i--) {
      const ref = annotations.get(i), annotation = ctx.lookup(ref, PDFDict);
      const rect = numberArray(annotation.lookupMaybe(PDFName.of('Rect'), PDFArray));
      const subtype = annotation.get(PDFName.of('Subtype'))?.toString();
      if (['/FileAttachment', '/RichMedia', '/Movie', '/Sound', '/Screen'].includes(subtype) ||
        rect && pageRegions.some((region) => overlaps([Math.min(rect[0], rect[2]), Math.min(rect[1], rect[3]), Math.max(rect[0], rect[2]), Math.max(rect[1], rect[3])], region.rect))) {
        if (annotation.get(PDFName.of('Subtype'))?.toString() === '/Widget') removedWidgets.add(ref.toString());
        removedAnnots.add(ref.toString());
        annotations.remove(i);
      } else if (subtype === '/Link') {
        resolveDestination(annotation, 'Dest');
        const action = annotation.lookupMaybe(PDFName.of('A'), PDFDict);
        if (action?.get(PDFName.of('S'))?.toString() === '/GoTo') resolveDestination(action, 'D');
      }
    }
    page.node.delete(PDFName.of('Metadata'));
    page.node.delete(PDFName.of('PieceInfo'));
    page.node.delete(PDFName.of('Thumb'));
  }
  // Popups and replies can retain the removed annotation through a backref.
  let changed;
  do {
    changed = false;
    for (const page of document.getPages()) {
      const annotations = page.node.Annots();
      if (!annotations) continue;
      for (let i = annotations.size() - 1; i >= 0; i--) {
        const ref = annotations.get(i), annotation = ctx.lookup(ref, PDFDict);
        const related = annotation.get(PDFName.of('IRT')) ||
          (annotation.get(PDFName.of('Subtype'))?.toString() === '/Popup' && annotation.get(PDFName.of('Parent')));
        if (related && removedAnnots.has(related.toString())) {
          removedAnnots.add(ref.toString()); annotations.remove(i); changed = true;
        }
      }
    }
  } while (changed);
  // Prune redacted widgets from the canonical form tree as well as the page.
  const prune = (array) => {
    if (!array) return;
    for (let i = array.size() - 1; i >= 0; i--) {
      const ref = array.get(i), field = ctx.lookup(ref, PDFDict);
      const kids = field.lookupMaybe(PDFName.of('Kids'), PDFArray);
      if (kids) prune(kids);
      if (removedWidgets.has(ref.toString()) || kids?.size() === 0) array.remove(i);
    }
  };
  const form = document.catalog.lookupMaybe(PDFName.of('AcroForm'), PDFDict);
  form?.delete(PDFName.of('XFA'));
  prune(form?.lookupMaybe(PDFName.of('Fields'), PDFArray));
  if (form?.lookupMaybe(PDFName.of('Fields'), PDFArray)?.size() === 0) document.catalog.delete(PDFName.of('AcroForm'));
  // Metadata, embedded files, scripts and editing histories can carry copies
  // of removed content. The redaction UI explicitly explains this cleanup.
  ctx.trailerInfo.Info = undefined;
  for (const key of ['Metadata', 'PieceInfo', 'Names', 'Dests', 'AF', 'OpenAction', 'AA', 'StructTreeRoot', 'Outlines', 'Threads', 'SpiderInfo', 'Collection']) document.catalog.delete(PDFName.of(key));
  // Page/annotation/resource metadata and actions may live below the catalog.
  for (const [, object] of ctx.enumerateIndirectObjects()) {
    const dict = object instanceof PDFDict ? object : object.dict;
    if (!(dict instanceof PDFDict)) continue;
    for (const key of ['Metadata', 'PieceInfo', 'AF', 'AA']) dict.delete(PDFName.of(key));
    const action = dict.lookupMaybe(PDFName.of('A'), PDFDict);
    if (action && !['/URI', '/GoTo'].includes(action.get(PDFName.of('S'))?.toString())) dict.delete(PDFName.of('A'));
    // /Next can chain a script behind an otherwise ordinary link action.
    action?.delete(PDFName.of('Next'));
  }
}

export async function prepareTextRemoval(bytes, edits, options) {
  const targets = removalTargets(edits, options);
  if (!targets.length) return { bytes, edits };
  const output = await removePdfContent(bytes, targets);
  // Stop future saves from removing this source region again. Old editable
  // metadata must also be replaced: it can contain the previous text value.
  const updated = edits.map((edit) => removalTargets([edit], options).length ? {
    ...edit, modified: true, sourceRemoved: true, originalBounds: undefined, sourceTextBounds: undefined, bakedBounds: undefined,
  } : edit);
  const document = await PDFDocument.load(output);
  const old = await extractOverlayMetadata(document);
  if (old) await embedOverlayMetadata(document, updated, old.annotations, document.getPageCount());
  return { bytes: await document.save({ updateFieldAppearances: false }), edits: updated };
}
